import baseUrl from '../baseUrl';
import {LocalAuthTokens} from './localAuthTypes';
import {isEmbeddedInExtension} from '../../lib/extensionEmbed';

export type RefreshOutcome =
    | {status: 'success'; tokens: LocalAuthTokens}
    | {status: 'expired'} // 400/401 — definitive, session is dead
    | {status: 'transient'}; // network error / 5xx / 408 / 429 — retry later

const delay = (ms: number) =>
    new Promise<void>(resolve => {
        window.setTimeout(resolve, ms);
    });

/**
 * Ceiling on a single refresh request. Every authenticated call in the app funnels
 * through token acquisition, so a refresh fetch that stalls without erroring would
 * freeze the whole tab (message sends, the heartbeat that clears the typing spinner,
 * everything). A timeout abort throws, which the caller already classifies as
 * transient and retries with backoff.
 */
const REFRESH_FETCH_TIMEOUT_MS = 15_000;

/**
 * Ceiling on waiting for another tab's refresh to finish. A healthy holder completes
 * one request well inside this; a holder stuck on a hung request must not freeze
 * every other tab queued behind it.
 */
const REFRESH_LOCK_WAIT_TIMEOUT_MS = 30_000;

/**
 * Calls the refresh endpoint and classifies the result so callers can
 * distinguish "session is dead, clear and re-auth" (expired) from
 * "try again later" (transient).
 *
 * The refresh credential is the HttpOnly cookie the browser attaches via
 * credentials:'include'. The X-TA-Auth header is required before the server
 * honours the cookie — it forces a CORS preflight, so cross-site pages can
 * never trigger a cookie-authenticated rotation. A legacy sessionStorage
 * refresh token may be passed to migrate a pre-cookie session.
 */
export async function refreshLocalToken(
    legacyRefreshToken?: string
): Promise<RefreshOutcome> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-TA-Auth': '1'
        };
        if (isEmbeddedInExtension()) {
            headers['X-Running-In-Extension'] = 'true';
        }

        const response = await fetch(`${baseUrl}/api/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({refreshToken: legacyRefreshToken ?? ''}),
            signal: AbortSignal.timeout(REFRESH_FETCH_TIMEOUT_MS)
        });

        if (response.ok) {
            const tokens: LocalAuthTokens = await response.json();
            return {status: 'success', tokens};
        }

        // 400 (no cookie/malformed) and 401 (expired/invalid/revoked) are
        // definitive — the server has rejected the session and a retry with the
        // same credentials will never succeed.
        if (response.status === 400 || response.status === 401) {
            return {status: 'expired'};
        }

        // Anything else (5xx, 408, 429, ...) is treated as transient.
        return {status: 'transient'};
    } catch {
        // fetch threw — network down, CORS, aborted, etc. Transient.
        return {status: 'transient'};
    }
}

// Refresh rotates the server-side token, so concurrent attempts from multiple
// tabs are serialised through a cross-tab lock where the browser supports it.
// The server also keeps a short rotation grace window, so the rare unsupported
// browser still cannot lock anyone out.
//
// The WAIT for the lock is bounded: the lock is browser-wide per origin, so a tab
// whose refresh request hung would otherwise freeze token acquisition in every
// other tab queued behind it. On timeout the refresh proceeds without the lock;
// the server's rotation grace window tolerates the resulting concurrency. Once
// granted, the signal no longer applies and fn runs to completion.
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    if (typeof navigator !== 'undefined' && navigator.locks) {
        try {
            return await navigator.locks.request(
                'ta-refresh',
                {signal: AbortSignal.timeout(REFRESH_LOCK_WAIT_TIMEOUT_MS)},
                () => fn()
            );
        } catch (error) {
            // Only a timed-out WAIT falls through to lock-less execution; a
            // failure from fn itself (lock granted) must propagate unchanged.
            const isAborted =
                error instanceof DOMException &&
                (error.name === 'AbortError' || error.name === 'TimeoutError');
            if (!isAborted) {
                throw error;
            }
        }
    }
    return fn();
}

/**
 * Refreshes the local JWT, retrying only on transient failures with exponential
 * backoff. Never retries an 'expired' outcome (it will never succeed).
 */
export async function refreshLocalTokenWithRetry(
    legacyRefreshToken?: string,
    {
        retries = 2,
        baseDelayMs = 1000
    }: {retries?: number; baseDelayMs?: number} = {}
): Promise<RefreshOutcome> {
    return withRefreshLock(async () => {
        let attempt = 0;
        let outcome = await refreshLocalToken(legacyRefreshToken);

        while (outcome.status === 'transient' && attempt < retries) {
            await delay(baseDelayMs * 2 ** attempt);
            attempt += 1;
            outcome = await refreshLocalToken(legacyRefreshToken);
        }

        return outcome;
    });
}
